import Loading from '../../Loading/Loading';
import Show from '../../Show/Show';
import { BasicModalBelowWithButton } from '../../Modal/BasicModalBelowWithButton';
import { COLORS } from '../../../config/designSystem/Colors';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FlatList, Image, ImageProps, StyleSheet, TouchableWithoutFeedback, View, useWindowDimensions} from 'react-native';
import { H3, ParagraphLBold, ParagraphMRegular } from '../../../config/designSystem/typography';
import { IBaseTranslatableModel } from '../../../state/stores/Question/BaseTranslatableModel';
import { IStaticFrameModel } from '../../../state/stores/Question/StaticFrameModel';
import { MIN_WIDTH } from '../../../config/designSystem/MinWidth';
import { SPACING } from '../../../config/designSystem/Spacing';
import { SnapshotOrInstance } from 'mobx-state-tree';
import { TouchableText } from '../../TouchableText/TouchableText';
import { frameWithPicture, frameWithTitle } from './StaticFrameConfig';
import { logAmplitude } from '../../../utils/analytics/analytics';
import { observer } from 'mobx-react';
import { useStores } from '../../../hooks/useStores';


interface StaticFrameProps {
  staticFrame: Partial<SnapshotOrInstance<IStaticFrameModel>>;
  renderImage?: (props: ImageProps) => ReactNode;
  loading?: boolean;
}
const StaticFrame: FC<StaticFrameProps> = ({
  staticFrame: {
    mainTitle = '',
    secondaryTitle = '',
    thirdTitle = '',
    text = [],
    type = 'tit_pic_xText',
    image = '',
  },
  renderImage: renderImageProp,
  loading,
}) => {
  const rootStore = useStores();
  const [modalBelow, setModalBelow] = useState<boolean>(false);
  // a frame without a title is its picture (often a whole infographic):
  // shown at the content width, with the picture's own proportions
  const [pictureRatio, setPictureRatio] = useState<number>(1);
  useEffect(() => {
    if (!image || mainTitle) {
      return;
    }
    Image.getSize(image, (w, h) => w && h && setPictureRatio(w / h), () => undefined);
  }, [image, mainTitle]);
  const { width } = useWindowDimensions();
  const labels = rootStore?.language.translate.MOBILE?.SESSION;

  const openModal = () => {
    logAmplitude('Static Frame Modal Opened', {
      on: rootStore.treeNav.current,
      mainTitle,
    });
    setModalBelow(true);
  };

  const closeModal = () => {
    setModalBelow(false);
  };

  const renderMainTitle = () => {
    return(
      <Show when={mainTitle && frameWithTitle.includes(type)}>
        <H3 color={COLORS.content.primary} align={true}>{mainTitle}</H3>
      </Show>
    );};

  const renderImage = () => {
    return(
      <>
        <Show when={image && frameWithPicture.includes(type)}>
          <Image
            source={{uri: image}}
            style={mainTitle ? styles.image : [styles.picture, {aspectRatio: pictureRatio}]}
            resizeMode='contain'
          />
        </Show>
        <Show when={renderImageProp} keyed>
          {render => render({style: styles.image})}
        </Show>
      </>
    );
  };

  const renderSecondaryAndThirdTitle = () => {
    if('tit_pic_xIconText' === type) {
      return(
        <Show when={secondaryTitle}>
          <View style={styles.secondaryAndThirdTitle}>
            <ParagraphMRegular>{secondaryTitle}</ParagraphMRegular>
            <Show when={thirdTitle}>
              <TouchableText onPress={openModal} label={thirdTitle}>
              </TouchableText>
            </Show>
          </View>
        </Show>

      );
    } else {
      return(
        <Show when={secondaryTitle}>
          <ParagraphMRegular>{secondaryTitle}</ParagraphMRegular>
        </Show>

      );
    }};

  const renderItemOfModalBelow = ({item, index} : {item:IBaseTranslatableModel, index: number}) => {
    return(<TouchableWithoutFeedback onPress={closeModal} >
      <View style={styles.itemOfModalBelow}>
        <ParagraphMRegular align={true}>
          {item.value}
        </ParagraphMRegular>
        <ParagraphMRegular align={true}>
          {item.secondaryValue}
        </ParagraphMRegular>
      </View>

    </TouchableWithoutFeedback>);
  };

  const renderModalBelow = () => {

    return(
      <Show when={type === 'tit_pic_xIconText'} >
        <BasicModalBelowWithButton
          isVisible={modalBelow}
          onDismiss={closeModal}
          labelButton={labels?.IN_SESSION_OK}
        >
          <ParagraphLBold color={COLORS.content.primary} align={true}>
            {thirdTitle}
          </ParagraphLBold>
          <FlatList
            data={text}
            renderItem={renderItemOfModalBelow}
            keyExtractor={(item, idx) => String(idx)}
            keyboardShouldPersistTaps='always'
            keyboardDismissMode='on-drag'
          />
        </BasicModalBelowWithButton>
      </Show>);

  };



  return (
    <>
      <View style={[
        styles.mainView,
        width > MIN_WIDTH.md && styles.mainViewSmall,
        width >= MIN_WIDTH.md && styles.mainViewLarge,
      ]}>
        {renderMainTitle()}
        <Show when={!loading} fallback={<Loading />}>
          {renderImage()}
          {renderSecondaryAndThirdTitle()}
        </Show>
      </View>
      {renderModalBelow()}
    </>
  );
};
export default observer(StaticFrame);

const styles = StyleSheet.create({
  mainView: {
    alignItems: 'center',
  },
  mainViewSmall: {
    marginHorizontal: SPACING['sp-16'],
  },
  mainViewLarge: {
    marginHorizontal: SPACING['sp-126'],
  },
  image: {
    height: 120,
    width: 120,
    marginVertical: 48,
    justifyContent: 'center'
  },
  picture: {
    width: '100%',
    maxWidth: 480,
    marginVertical: 24,
  },
  secondaryAndThirdTitle: {rowGap: 32, alignItems:'center'},
  itemOfModalBelow: {
    rowGap:10, alignItems:'center', marginVertical: 16
  },
});
